import { confirmAction } from '../../components/ui/Confirm.js';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useQueues } from '../../lib/api/hooks-tms.js';
import { TableSkeleton, Empty, ErrorBanner, Chip, ChipList } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { formatDuration } from '../../lib/format.js';

/**
 * The roster: which queues each person answers, and how they have been doing.
 *
 * The inverse of the Queues screen, over the same rows. A supervisor asks about
 * a person -- "is Sarah on the sales queue, and should she be" -- and answering
 * that from a list of queues means opening each one in turn.
 *
 * TIERS ARE THE SKILLS. mod_callcenter rings tier 1 first and only falls to
 * tier 2 when tier 1 is exhausted, so "first choice / backup / last resort" is
 * the same statement as a tier number. Labelling them that way is honest;
 * inventing a separate skills vocabulary on top would be a second name for one
 * mechanism.
 */

interface Membership {
  membership_id: string;
  queue_id: string;
  queue_name: string;
  tier_level: number;
  tier_position: number;
}
interface Agent {
  extension_id: string;
  number: string;
  display_name: string | null;
  queues: Membership[];
  answered: number | null;
  talk_sec: number | null;
  /** Whether this seat's type includes queue membership; the API refuses the rest. */
  queue_member: boolean;
}

const TIER_LABEL: Record<number, string> = {
  1: 'first choice', 2: 'backup', 3: 'last resort',
};
const tierLabel = (n: number) => TIER_LABEL[n] ?? `tier ${n}`;

export function AgentsTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  // The key the agents API checks (modules/tms/agents.ts), not queue:write.
  // Rostering people is kept apart from editing a queue's settings, and a
  // control gated on the wrong key is offered to people the API will refuse.
  const writable = can('queue:agent:manage');
  const [days, setDays] = useState('7');
  const [adding, setAdding] = useState<string | null>(null);

  const key = ['tenants', tenantId, 'agents', days] as const;
  const agents = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ data: Agent[]; window_days: number }>(
      `/v1/tenants/${tenantId}/agents?days=${days}`),
  });
  const queues = useQueues(tenantId);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['tenants', tenantId, 'agents'] });

  const assign = useMutation({
    mutationFn: ({ extensionId, queueId, tier }: {
      extensionId: string; queueId: string; tier: number;
    }) => api.put(
      `/v1/tenants/${tenantId}/agents/${extensionId}/queues/${queueId}`,
      { tier_level: tier }),
    onSuccess: () => { setAdding(null); invalidate(); },
  });
  const remove = useMutation({
    mutationFn: ({ extensionId, queueId }: { extensionId: string; queueId: string }) =>
      api.delete(`/v1/tenants/${tenantId}/agents/${extensionId}/queues/${queueId}`),
    onSuccess: invalidate,
  });

  if (agents.error) return <ErrorBanner error={agents.error} />;

  const rows = agents.data?.data ?? [];
  const staffed = rows.filter((a) => a.queues.length > 0);
  const allQueues = queues.data?.data ?? [];

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Agents &amp; skills</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            {staffed.length} of {rows.length} seat{rows.length === 1 ? '' : 's'} answer a queue.
            Tier decides who rings first.
          </p>
        </div>
        <select className="arc-select" style={{ width: 'auto' }} value={days}
          onChange={(e) => setDays(e.target.value)} aria-label="Statistics window">
          <option value="1">Today</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
        </select>
      </div>

      {allQueues.length === 0 && (
        <p className="arc-note">
          There are no queues yet, so nobody can be rostered onto one.
        </p>
      )}

      <div className="arc-card">
        {agents.data === undefined ? (
          <TableSkeleton query={agents} rows={5} cols={4} />
        ) : rows.length === 0 ? (
          <Empty icon="users" title="No active extensions" />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Agent</th><th>Answers</th>
                  <th>Calls taken</th><th>Talk time</th>
                  {writable && <th style={{ width: 160 }} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.extension_id} style={a.queues.length ? undefined : { opacity: 0.6 }}>
                    <td>
                      <div style={{ fontWeight: 550 }}>{a.display_name ?? a.number}</div>
                      <div className="arc-subtle arc-mono" style={{ marginTop: 2 }}>{a.number}</div>
                    </td>
                    <td>
                      {a.queues.length === 0 ? (
                        <span className="arc-subtle">no queues</span>
                      ) : (
                        <ChipList>
                          {a.queues.map((m) => (
                            <Chip key={m.membership_id} detail={`· ${tierLabel(m.tier_level)}`}
                              title={`${m.queue_name} — ${tierLabel(m.tier_level)}`}
                              {...(writable ? {
                                // Live routing changes at once, so it asks first (UX review, 21 October 2026).
                                onRemove: () => {
                                  void confirmAction({
                                    title: `Take ${a.display_name ?? a.number} off ${m.queue_name}?`,
                                    body: 'Calls in that queue stop ringing them straight away.',
                                    action: 'Take off', danger: true,
                                  }).then((ok) => { if (ok) remove.mutate({ extensionId: a.extension_id, queueId: m.queue_id }); });
                                },
                                removeLabel: `Remove from ${m.queue_name}`,
                              } : {})}>
                              {m.queue_name}
                            </Chip>
                          ))}
                        </ChipList>
                      )}
                    </td>
                    {/*
                      A dash, not a zero. "No activity recorded" and "took no
                      calls" look identical as 0 and mean different things --
                      the first usually means the queue is new.
                    */}
                    <td className="arc-num">
                      {a.answered === null ? <span className="arc-subtle">—</span> : a.answered}
                    </td>
                    <td className="arc-subtle">
                      {a.talk_sec === null ? '—' : formatDuration(a.talk_sec)}
                    </td>
                    {writable && (
                      <td>
                        {!a.queue_member ? (
                          /* Not offered, because the API refuses it: queue membership is part
                             of the seat's type (owner, 16 September 2026). */
                          <span className="arc-subtle" style={{ fontSize: 11.5 }}
                            title="Change this extension’s type, or add Queue membership on its Features section.">
                            can't answer queues
                          </span>
                        ) : adding === a.extension_id ? (
                          <AddToQueue
                            queues={allQueues.filter(
                              (q) => !a.queues.some((m) => m.queue_id === q.id))}
                            pending={assign.isPending}
                            onCancel={() => setAdding(null)}
                            onPick={(queueId, tier) => assign.mutate({
                              extensionId: a.extension_id, queueId, tier,
                            })}
                          />
                        ) : (
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            disabled={allQueues.length === a.queues.length}
                            onClick={() => setAdding(a.extension_id)}>
                            <Icon name="plus" size={12} /> Add to queue
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function AddToQueue({ queues, pending, onCancel, onPick }: {
  queues: Array<{ id: string; name: string }>;
  pending: boolean;
  onCancel: () => void;
  onPick: (queueId: string, tier: number) => void;
}) {
  const [queueId, setQueueId] = useState('');
  const [tier, setTier] = useState('1');

  return (
    <div className="arc-row" style={{ gap: 4, flexWrap: 'wrap' }}>
      <select className="arc-select" style={{ width: 'auto' }} value={queueId} aria-label="Queue"
        disabled={pending} onChange={(e) => setQueueId(e.target.value)}>
        <option value="">Queue…</option>
        {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
      </select>
      <select className="arc-select" style={{ width: 'auto' }} value={tier} aria-label="Priority"
        disabled={pending} onChange={(e) => setTier(e.target.value)}>
        <option value="1">first choice</option>
        <option value="2">backup</option>
        <option value="3">last resort</option>
      </select>
      <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
        disabled={!queueId || pending} onClick={() => onPick(queueId, Number(tier))}>
        Add
      </button>
      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}
